import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { captureExceptionSafely } from '@/helpers/capture-exception-safely';

import type { UpdateCompanyTagsPayload } from '@/api/v4/organization-tags.api';
import { updateOrganizationTags } from '@/api/v4/organization-tags.api';
import { queryClient, QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';
import type { CompanyProjectsListings } from '@/api/v4/organization-projects-listings.api';

const getCompanyListings = (organizationId: string) => {
  return (
    queryClient.getQueryData<CompanyProjectsListings>([
      QueryKey.CompanyProjectsListings,
      organizationId,
    ])?.listings ?? []
  );
};

export const useUpdateCompanyTags = () => {
  const { t } = useTranslation('default');

  const updateTagsMutation = useMutation({
    mutationFn: ({
      organizationId,
      payload,
    }: {
      organizationId: string;
      payload: UpdateCompanyTagsPayload;
    }) => {
      return updateOrganizationTags(organizationId, payload);
    },
    onError: (error: Error) => {
      notify({
        message: t`unknownError`,
      });
      captureExceptionSafely(error);
    },
    onSuccess: async (_data, variables) => {
      const { organizationId } = variables;
      const companyListings = getCompanyListings(organizationId);

      if (companyListings?.length > 0) {
        await Promise.all(
          companyListings.map(listing =>
            queryClient.invalidateQueries({
              queryKey: [QueryKey.ProjectListings, listing.project.id],
            }),
          ),
        );
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKey.CompanyTags] }),
        queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.LastActivitiesCount],
        }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.ProjectListings, organizationId],
        }),
      ]);
    },
  });

  const updateTags = (
    organizationId: string,
    payload: UpdateCompanyTagsPayload,
  ) =>
    updateTagsMutation.mutateAsync({
      organizationId,
      payload,
    });

  return {
    updateTags,
    isError: updateTagsMutation.isError,
  };
};
